/**
 * NAZ AI - Vehicle Identity Gate System v2.2
 * Core Identity Validation Engine
 * 
 * THREE STATES ONLY:
 * - IDENTITY_ABSENT: Missing fields OR confidence = Low → BLOCK
 * - IDENTITY_PENDING: All fields BUT confidence = Medium → BLOCK + Ask confirmation
 * - IDENTITY_CONFIRMED: All fields AND confidence = High → UNLOCK
 */

export type IdentityState = 'IDENTITY_ABSENT' | 'IDENTITY_PENDING' | 'IDENTITY_CONFIRMED';
export type ConfidenceLevel = 'High' | 'Medium' | 'Low';
export type Market = 'GCC' | 'Africa' | 'Europe' | 'Japan' | 'USA' | 'Other';

export interface VehicleIdentity {
    identity_id: string;
    vin: string | null;
    vin_checksum_valid: boolean;
    make: string;
    model: string;
    year: number;
    engine: string; // Exact code: 1ZR-FE, 1HZ, etc.
    engine_swap_confirmed: boolean;
    market: Market;
    confidence: ConfidenceLevel;
    confidence_score: number; // 0-1
    sources: string[]; // ['vin_ocr', 'vin_text', 'manual_entry', 'user_confirmed']
    conflicts: string[];
    notes: string;
    validated_at: string | null;
    last_activity_at: string;
    user_confirmed: boolean;
}

export interface IdentityInputs {
    vin_image?: string; // Base64 OCR input
    vin_text?: string; // Direct VIN input
    manual_entry?: {
        make?: string;
        model?: string;
        year?: number;
        engine?: string;
    };
    uploaded_file?: any; // PDF/Image with vehicle data
    conversation_history?: any[]; // Context extraction
    vin_decoded_data?: any; // From NHTSA API
}

export interface ValidationResult {
    state: IdentityState;
    canDiagnose: boolean;
    reason?: string;
    prompt_ar?: string;
    prompt_en?: string;
    identity?: VehicleIdentity;
}

/**
 * VIN Checksum Validator (ISO 3779)
 */
export function validateVINChecksum(vin: string): boolean {
    if (!vin || vin.length !== 17) return false;

    const transliteration = '0123456789X';
    const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
    const values: Record<string, number> = {
        '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
        'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8,
        'J': 1, 'K': 2, 'L': 3, 'M': 4, 'N': 5, 'P': 7, 'R': 9,
        'S': 2, 'T': 3, 'U': 4, 'V': 5, 'W': 6, 'X': 7, 'Y': 8, 'Z': 9
    };

    let sum = 0;
    for (let i = 0; i < 17; i++) {
        const vinChar = vin[i];
        if (!vinChar) return false;
        const char = vinChar.toUpperCase();
        const value = values[char];
        if (value === undefined) return false;
        const weight = weights[i];
        if (weight === undefined) return false;
        sum += value * weight;
    }

    const checkDigit = vin[8];
    const calculatedCheck = transliteration[sum % 11];

    return checkDigit === calculatedCheck;
}

/**
 * Engine Swap Detection
 */
export function detectEngineSwap(vinEngine: string | null, manualEngine: string | null): {
    isSwap: boolean;
    prompt_ar: string;
    prompt_en: string;
} {
    if (!vinEngine || !manualEngine) {
        return { isSwap: false, prompt_ar: '', prompt_en: '' };
    }

    const normalized1 = vinEngine.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const normalized2 = manualEngine.toUpperCase().replace(/[^A-Z0-9]/g, '');

    const isSwap = normalized1 !== normalized2;

    if (isSwap) {
        return {
            isSwap: true,
            prompt_ar: `لقيت العربية من الشاصي بمحرك ${vinEngine}، لكنك ذكرت ${manualEngine}.\nهل المكنة مسقطة/مغيرة؟ أكد لي عشان نشتغل صح.`,
            prompt_en: `VIN shows ${vinEngine} engine, but you mentioned ${manualEngine}.\nIs this an engine swap? Please confirm for accurate diagnosis.`
        };
    }

    return { isSwap: false, prompt_ar: '', prompt_en: '' };
}

/**
 * Evidence Weighting System
 */
function calculateEvidenceWeight(sources: string[], hasVin: boolean, vinValid: boolean, manualComplete: boolean): number {
    let weight = 0;

    // Full VIN with valid checksum
    if (hasVin && vinValid) {
        weight = 0.95;
    }
    // Partial VIN (8-16 chars)
    else if (hasVin && !vinValid) {
        weight = 0.60;
    }
    // Manual entry (all fields)
    else if (manualComplete) {
        weight = 0.70;
    }
    // OCR from image
    else if (sources.includes('vin_ocr')) {
        weight = 0.50;
    }

    // User confirmation boost
    if (sources.includes('user_confirmed')) {
        weight = Math.min(weight + 0.20, 1.0);
    }

    return weight;
}

/**
 * Conflict Resolution Matrix
 */
function resolveConflicts(
    vinData: any,
    manualData: any
): { conflicts: string[]; notes: string } {
    const conflicts: string[] = [];
    let notes = '';

    if (!vinData || !manualData) return { conflicts, notes };

    // Make mismatch
    if (vinData.make && manualData.make && vinData.make !== manualData.make) {
        conflicts.push(`VIN Make (${vinData.make}) ≠ Manual Make (${manualData.make})`);
        notes += 'Trusting VIN make. ';
    }

    // Year mismatch
    if (vinData.year && manualData.year) {
        const yearDiff = Math.abs(vinData.year - manualData.year);
        if (yearDiff === 1) {
            notes += 'Model-year difference noted. ';
        } else if (yearDiff > 1) {
            conflicts.push(`VIN Year (${vinData.year}) ≠ Manual Year (${manualData.year})`);
        }
    }

    // Engine mismatch (handled separately by detectEngineSwap)

    return { conflicts, notes };
}

/**
 * Session Timeout Check with Soft Warning (45 min) and Hard Reset (60 min)
 */
function checkSessionTimeout(lastActivity: string): {
    isExpired: boolean;
    minutesElapsed: number;
    warningLevel: 'ACTIVE' | 'SOFT_WARNING' | 'HARD_RESET';
} {
    const now = new Date().getTime();
    const lastTime = new Date(lastActivity).getTime();
    const diffMinutes = (now - lastTime) / (1000 * 60);

    let warningLevel: 'ACTIVE' | 'SOFT_WARNING' | 'HARD_RESET' = 'ACTIVE';
    if (diffMinutes >= 60) {
        warningLevel = 'HARD_RESET';
    } else if (diffMinutes >= 45) {
        warningLevel = 'SOFT_WARNING';
    }

    return {
        isExpired: diffMinutes > 60,
        minutesElapsed: Math.floor(diffMinutes),
        warningLevel
    };
}

/**
 * Sudanese Context Validator
 * Common vehicles and engines in Sudan
 */
function validateSudanContext(make: string, model: string, engine: string): number {
    const commonVehicles = [
        { make: 'TOYOTA', models: ['HILUX', 'LAND CRUISER', 'COROLLA', 'HIACE'] },
        { make: 'MITSUBISHI', models: ['L200', 'PAJERO'] },
        { make: 'NISSAN', models: ['PATROL', 'NAVARA'] }
    ];

    const commonEngines = ['1HZ', '1HD-T', '1KD-FTV', '2KD-FTV', '1ZR-FE', '2ZR-FE', '1NZ-FE', '2NZ-FE', '1GR-FE', '2TR-FE', '4D56'];

    let bonus = 0;

    // Check if vehicle is common in Sudan
    const makeUpper = make.toUpperCase();
    const modelUpper = model.toUpperCase();
    const vehicleMatch = commonVehicles.find(v => v.make === makeUpper && v.models.some(m => modelUpper.includes(m)));
    if (vehicleMatch) bonus += 0.05;

    // Check if engine is verified
    const engineUpper = engine.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (commonEngines.some(e => engineUpper.includes(e))) bonus += 0.10;

    return bonus;
}

/**
 * Process Identity from Multiple Inputs
 */
export function processIdentity(inputs: IdentityInputs): VehicleIdentity {
    const sources: string[] = [];
    let vin: string | null = null;
    let vinChecksumValid = false;
    let make = '';
    let model = '';
    let year = 0;
    let engine = '';
    let vinEngine: string | null = null;
    let market: Market = 'Other';

    // 1. VIN Text Input
    if (inputs.vin_text) {
        vin = inputs.vin_text.toUpperCase().trim();
        vinChecksumValid = validateVINChecksum(vin);
        sources.push('vin_text');
    }

    // 2. VIN Decoded Data (from NHTSA)
    if (inputs.vin_decoded_data) {
        const decoded = inputs.vin_decoded_data;
        make = decoded.make || make;
        model = decoded.model || model;
        year = decoded.year || year;
        vinEngine = decoded.engine || null;
        sources.push('vin_decoded');
    }

    // 3. Manual Entry
    if (inputs.manual_entry) {
        make = inputs.manual_entry.make || make;
        model = inputs.manual_entry.model || model;
        year = inputs.manual_entry.year || year;
        engine = inputs.manual_entry.engine || engine;
        sources.push('manual_entry');
    }

    // 4. VIN Image OCR (placeholder - would need OCR service)
    if (inputs.vin_image) {
        sources.push('vin_ocr');
    }

    // Determine market
    if (make.toUpperCase().includes('TOYOTA') || make.toUpperCase().includes('NISSAN')) {
        market = 'GCC'; // Common in Sudan via GCC imports
    }

    // Calculate confidence
    const hasVin = vin !== null && vin.length >= 11;
    const manualComplete = !!(make && model && year && engine);
    const evidenceWeight = calculateEvidenceWeight(sources, hasVin, vinChecksumValid, manualComplete);

    // Resolve conflicts
    const vinData = inputs.vin_decoded_data;
    const manualData = inputs.manual_entry;
    const { conflicts, notes } = resolveConflicts(vinData, manualData);

    // Detect engine swap
    const swapDetection = detectEngineSwap(vinEngine, engine);
    const engineSwapConfirmed = swapDetection.isSwap && sources.includes('user_confirmed');

    // Sudan context bonus
    const sudanBonus = validateSudanContext(make, model, engine);
    const finalScore = Math.min(evidenceWeight + sudanBonus, 1.0);

    // Determine confidence level
    let confidence: ConfidenceLevel = 'Low';
    if (finalScore >= 0.85 && conflicts.length === 0 && manualComplete) {
        confidence = 'High';
    } else if (finalScore >= 0.60 && manualComplete) {
        confidence = 'Medium';
    }

    // Override: Valid VIN + checksum + engine matches = High
    if (hasVin && vinChecksumValid && vinEngine && engine && !swapDetection.isSwap) {
        confidence = 'High';
    }

    // Override: Valid VIN + user confirmed swap = High
    if (hasVin && vinChecksumValid && engineSwapConfirmed) {
        confidence = 'High';
    }

    const identity: VehicleIdentity = {
        identity_id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        vin,
        vin_checksum_valid: vinChecksumValid,
        make,
        model,
        year,
        engine: engine || vinEngine || '',
        engine_swap_confirmed: engineSwapConfirmed,
        market,
        confidence,
        confidence_score: finalScore,
        sources,
        conflicts,
        notes: notes + (swapDetection.isSwap ? swapDetection.prompt_ar : ''),
        validated_at: confidence === 'High' ? new Date().toISOString() : null,
        last_activity_at: new Date().toISOString(),
        user_confirmed: sources.includes('user_confirmed')
    };

    return identity;
}

/**
 * Validate Identity State
 * Returns THREE_STATES and blocking decision
 */
export function validateIdentity(identity: VehicleIdentity): ValidationResult {
    // Check session timeout with soft warning
    if (identity.last_activity_at) {
        const timeoutCheck = checkSessionTimeout(identity.last_activity_at);

        // HARD RESET: > 60 minutes
        if (timeoutCheck.isExpired) {
            return {
                state: 'IDENTITY_ABSENT',
                canDiagnose: false,
                reason: 'session_timeout_hard',
                prompt_ar: 'طولنا ساكتين. لسه شغالين على نفس العربية؟',
                prompt_en: 'Session expired. Are we still working on the same vehicle?'
            };
        }

        // SOFT WARNING: 45-60 minutes
        if (timeoutCheck.warningLevel === 'SOFT_WARNING') {
            return {
                state: 'IDENTITY_PENDING',
                canDiagnose: false,
                reason: 'session_timeout_soft',
                prompt_ar: `⏱️ مر ${timeoutCheck.minutesElapsed} دقيقة من آخر نشاط.\n\nلسه شغالين على نفس العربية؟\n- قول "صح" للتأكيد\n- أو أدخل بيانات العربية الجديدة`,
                prompt_en: `⏱️ ${timeoutCheck.minutesElapsed} minutes since last activity.\n\nStill working on the same vehicle?\n- Reply "yes" to confirm\n- Or enter new vehicle information`,
                identity
            };
        }
    }

    // BLOCKING CONDITIONS
    const hasAllFields = !!(identity.make && identity.model && identity.year && identity.engine);
    const hasConflicts = identity.conflicts.length > 0;
    const isValidated = identity.validated_at !== null;

    // STATE 1: IDENTITY_ABSENT
    if (!hasAllFields || identity.confidence === 'Low') {
        return {
            state: 'IDENTITY_ABSENT',
            canDiagnose: false,
            reason: 'missing_fields',
            prompt_ar: `⚠️ NAZ AI - Vehicle Identity Required

عشان نعطيك تشخيص دقيق، لازم نعرف:

□ الماركة (Make): مثلاً Toyota, Mitsubishi
□ الموديل (Model): مثلاً Corolla, L200  
□ السنة (Year): مثلاً 2014
□ المحرك (Engine): كود دقيق مثل 1ZR-FE أو 1HZ

📎 أو صور لي استيكر الشاصي (VIN) لو موجود

❌ ما ممكن نكمل بدون هذه البيانات عشان نضمن سلامة العربية والفني.`,
            prompt_en: `⚠️ NAZ AI - Vehicle Identity Required

For accurate diagnosis, we need:

□ Make (e.g., Toyota, Mitsubishi)
□ Model (e.g., Corolla, L200)
□ Year (e.g., 2014)
□ Engine (exact code like 1ZR-FE or 1HZ)

📎 Or upload VIN sticker photo if available

❌ Cannot proceed without this data to ensure safety.`
        };
    }

    // STATE 2: IDENTITY_PENDING
    if (identity.confidence === 'Medium' || hasConflicts || !identity.user_confirmed) {
        const conflictNote = hasConflicts ? `\n\n⚠️ ${identity.conflicts.join(', ')}` : '';

        return {
            state: 'IDENTITY_PENDING',
            canDiagnose: false,
            reason: 'awaiting_confirmation',
            prompt_ar: `لقيت العربية: ${identity.year} ${identity.make} ${identity.model} بالمحرك ${identity.engine}

المصادر: ${identity.sources.join(', ')}${conflictNote}

أكد لي هذا صح عشان نكمل:
- قول "صح" أو "أكيد" للتأكيد
- أو صحح لي البيانات الغلط`,
            prompt_en: `I found: ${identity.year} ${identity.make} ${identity.model} with ${identity.engine} engine

Sources: ${identity.sources.join(', ')}${conflictNote}

Please confirm this is correct:
- Reply "yes" or "correct" to confirm
- Or correct any wrong information`,
            identity
        };
    }

    // STATE 3: IDENTITY_CONFIRMED
    if (identity.confidence === 'High' && hasAllFields && !hasConflicts && isValidated) {
        return {
            state: 'IDENTITY_CONFIRMED',
            canDiagnose: true,
            identity
        };
    }

    // Fallback to PENDING
    return {
        state: 'IDENTITY_PENDING',
        canDiagnose: false,
        reason: 'default_pending',
        identity
    };
}

/**
 * Update Identity with User Confirmation
 * Enhanced with Sudanese dialect terms
 */
export function confirmIdentity(identity: VehicleIdentity, userResponse: string): VehicleIdentity {
    const confirmWords = [
        // Standard Arabic
        'صح', 'أكيد', 'نعم', 'تمام',
        // English
        'yes', 'correct', 'confirm',
        // Sudanese Dialect
        'أيوه',      // Yes (Sudanese)
        'اي',        // Yes (short form)
        'صح كده',    // That's right
        'ما مشكلة',  // No problem (means yes/okay in Sudanese context)
        'ماشي',      // Okay
        'كويس',      // Good/Okay
        'تمام كده',  // Perfect like that
    ];

    const responseLower = userResponse.toLowerCase();
    const isConfirmed = confirmWords.some(word => responseLower.includes(word.toLowerCase()));

    if (isConfirmed) {
        return {
            ...identity,
            user_confirmed: true,
            confidence: 'High',
            validated_at: new Date().toISOString(),
            last_activity_at: new Date().toISOString(),
            sources: [...identity.sources, 'user_confirmed']
        };
    }

    return identity;
}

/**
 * Check for Vehicle Change in Conversation
 */
export function detectVehicleChange(message: string): boolean {
    const changeIndicators = [
        'عربية ثانية',
        'غيرت العربية',
        'سيارة جديدة',
        'different car',
        'new vehicle',
        'another car'
    ];

    return changeIndicators.some(indicator =>
        message.toLowerCase().includes(indicator.toLowerCase())
    );
}
